import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Clock, Trash, CalendarBlank, ListBullets, Sparkle, Warning, PaperPlaneTilt, Spinner, BellRinging, MagicWand, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import FristenKalender, { ART_LABEL, toLocalISO } from "@/components/FristenKalender";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

const TYP_LABEL = { verjaehrung: "Verjährung", einspruch: "Einspruchsfrist", zahlungsfrist: "Zahlungsfrist", regulierung: "Regulierungsfrist", gericht: "Gerichtliche Frist", sonstige: "Sonstige" };
const STATUS_META = {
  ueberfaellig: { label: "Überfällig", cls: "border-[#D32F2F] bg-[#FEF2F2]", badge: "bg-[#D32F2F] text-white" },
  heute: { label: "Heute fällig", cls: "border-[#F59E0B] bg-[#FFFBEB]", badge: "bg-[#F59E0B] text-white" },
  vorfrist: { label: "Vorfrist läuft", cls: "border-[#0EA5E9]/60 bg-[#F0F9FF]", badge: "bg-[#0EA5E9] text-white" },
  offen: { label: "Offen", cls: "border-[#E5E7EB] bg-white", badge: "bg-[#F3F4F6] text-[#4B5563]" },
  erledigt: { label: "Erledigt", cls: "border-[#E5E7EB] bg-[#FAFAFA]", badge: "bg-[#16A34A]/10 text-[#15803D]" },
};
const GROUP_ORDER = ["ueberfaellig", "heute", "vorfrist", "offen", "erledigt"];
const fmtD = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) : "—");
const relTage = (t) => (t == null ? "" : t < 0 ? `vor ${-t} Tg.` : t === 0 ? "heute" : t === 1 ? "morgen" : `in ${t} Tg.`);

const EMPTY_FORM = { titel: "", beschreibung: "", faellig_am: "", prioritaet: "mittel", akte_id: "", typ: "sonstige", vorfrist_tage: 7, rechtsgrundlage: "" };

function FristCard({ f, onToggle, onDelete }) {
  const m = STATUS_META[f.status] || STATUS_META.offen;
  return (
    <div className={`border rounded-lg p-4 flex items-start justify-between gap-4 ${m.cls}`} data-testid={`frist-${f.id}`}>
      <div className="flex items-start gap-3 min-w-0">
        <Checkbox checked={f.erledigt} onCheckedChange={() => onToggle(f)} className="mt-1" data-testid={`toggle-frist-${f.id}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`font-medium ${f.erledigt ? "line-through text-[#9CA3AF]" : "text-[#0A192F]"}`}>{f.titel}</p>
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${m.badge}`}>{m.label}</span>
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">{TYP_LABEL[f.typ] || f.typ}</span>
            {f.quelle === "ki" && <span className="inline-flex items-center gap-0.5 text-[10px] text-[#7C3AED]"><Sparkle size={10} weight="duotone" /> KI</span>}
          </div>
          {f.rechtsgrundlage && <p className="text-xs text-[#6B7280] mt-0.5 font-mono">{f.rechtsgrundlage}</p>}
          {f.beschreibung && <p className="text-sm text-[#4B5563] mt-1">{f.beschreibung}</p>}
          {f.akte && (
            <Link to={`/app/akten/${f.akte.id}`} className="inline-flex items-center gap-1 text-xs text-[#1A2238] hover:underline mt-1" data-testid={`frist-akte-${f.id}`}>
              <ArrowSquareOut size={12} /> {f.akte.aktenzeichen} · {f.akte.mandant_name || "—"}
            </Link>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <p className="font-mono text-sm">{fmtD(f.faellig_am)}</p>
          <p className="text-[11px] text-[#6B7280]">{relTage(f.tage)}{f.vorfrist_am && !f.erledigt ? ` · Vorfrist ${new Date(f.vorfrist_am + "T00:00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}` : ""}</p>
          <p className={`overline ${{ hoch: "text-[#D32F2F]", mittel: "text-[#F59E0B]", niedrig: "text-[#16A34A]" }[f.prioritaet] || ""}`}>{f.prioritaet}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onDelete(f.id)} data-testid={`delete-frist-${f.id}`}><Trash size={14} /></Button>
      </div>
    </div>
  );
}

function KiAssistent({ akten }) {
  const [frage, setFrage] = useState("");
  const [akteId, setAkteId] = useState("");
  const [antwort, setAntwort] = useState("");
  const [busy, setBusy] = useState(false);
  const QUICK = ["Welche Fristen sind diese Woche kritisch?", "Wann verjähren die Ansprüche in dieser Akte?", "Wie lange darf die Versicherung prüfen, bevor Verzug eintritt?", "Wie berechne ich die Einspruchsfrist beim Bußgeldbescheid?"];
  const ask = async (q) => {
    const text = (q || frage).trim();
    if (!text) return;
    setBusy(true); setAntwort("");
    try {
      const r = await api.post("/fristen/assistent", { frage: text, akte_id: akteId || undefined });
      setAntwort(r.data.antwort);
    } catch (err) { toast.error(errMsg(err, "KI-Assistent nicht erreichbar")); } finally { setBusy(false); }
  };
  return (
    <div className="bg-[#0A192F] text-white rounded-lg p-5 space-y-3" data-testid="fristen-ki-assistent">
      <div className="flex items-center gap-2">
        <Sparkle size={18} weight="duotone" className="text-[#C8A14F]" />
        <p className="font-serif text-lg">KI-Fristen-Assistent</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={akteId || "none"} onValueChange={v => setAkteId(v === "none" ? "" : v)}>
          <SelectTrigger className="sm:w-[240px] bg-white/10 border-white/20 text-white" data-testid="ki-akte"><SelectValue placeholder="Akte (optional)" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Alle Akten</SelectItem>
            {akten.map(a => <SelectItem key={a.id} value={a.id}>{a.aktenzeichen} · {a.mandant_name || "—"}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={frage} onChange={e => setFrage(e.target.value)} onKeyDown={e => e.key === "Enter" && ask()} placeholder="Frage zu Fristen stellen…" className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/50" data-testid="ki-frage" />
        <Button onClick={() => ask()} disabled={busy || !frage.trim()} className="bg-[#C8A14F] text-[#0A192F] hover:bg-[#D9B86A]" data-testid="ki-senden">
          {busy ? <Spinner size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {QUICK.map(q => (
          <button key={q} type="button" onClick={() => { setFrage(q); ask(q); }} className="text-[11px] px-2 py-1 rounded-full border border-white/20 hover:bg-white/10" data-testid="ki-quick">{q}</button>
        ))}
      </div>
      {antwort && <div className="text-sm leading-relaxed whitespace-pre-wrap bg-white/5 rounded-md p-3 border border-white/10" data-testid="ki-antwort">{antwort}</div>}
    </div>
  );
}

export default function Fristen() {
  const { user, setUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [akten, setAkten] = useState([]);
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("liste");
  const [monat, setMonat] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selected, setSelected] = useState(toLocalISO(new Date()));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [vorschlagAkte, setVorschlagAkte] = useState("");
  const [vorschlaege, setVorschlaege] = useState(null);
  const [vorschlagBusy, setVorschlagBusy] = useState(false);
  const [uebersicht, setUebersicht] = useState({ counts: {} });

  const load = async () => {
    try {
      const [f, a, u] = await Promise.all([api.get("/fristen"), api.get("/akten"), api.get("/fristen/uebersicht")]);
      setRows(f.data); setAkten(a.data); setUebersicht(u.data);
    } catch (err) { toast.error(errMsg(err, "Fristen konnten nicht geladen werden")); }
  };
  const loadKalender = async (m) => {
    const y = m.getFullYear(), mo = m.getMonth();
    // eine Woche Puffer für Randtage
    const von = toLocalISO(new Date(y, mo, -6)), bis = toLocalISO(new Date(y, mo + 1, 7));
    try { const r = await api.get("/fristen/kalender", { params: { von, bis } }); setEvents(r.data); } catch { /* */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadKalender(monat); /* eslint-disable-next-line */ }, [monat, rows.length]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, vorfrist_tage: Number(form.vorfrist_tage) || 0 };
      if (!payload.akte_id) delete payload.akte_id;
      if (!payload.rechtsgrundlage) delete payload.rechtsgrundlage;
      await api.post("/fristen", payload);
      toast.success("Frist angelegt");
      setOpen(false); setForm(EMPTY_FORM); load();
    } catch (err) { toast.error(errMsg(err, "Frist konnte nicht angelegt werden")); } finally { setSaving(false); }
  };
  const toggle = async (f) => { try { await api.patch(`/fristen/${f.id}`, { erledigt: !f.erledigt }); load(); } catch (err) { toast.error(errMsg(err, "Fehler")); } };
  const del = async (id) => { if (!window.confirm("Frist löschen?")) return; try { await api.delete(`/fristen/${id}`); load(); } catch (err) { toast.error(errMsg(err, "Löschen fehlgeschlagen")); } };

  const holeVorschlaege = async (akteId) => {
    if (!akteId) return;
    setVorschlagBusy(true);
    try { const r = await api.get(`/fristen/vorschlaege/${akteId}`); setVorschlaege(r.data); }
    catch (err) { toast.error(errMsg(err, "Vorschläge nicht verfügbar")); } finally { setVorschlagBusy(false); }
  };
  const uebernehmen = async (items) => {
    try {
      const r = await api.post("/fristen/bulk", { fristen: items });
      toast.success(`${r.data.angelegt} Frist${r.data.angelegt === 1 ? "" : "en"} übernommen`);
      setVorschlaege(null); load();
    } catch (err) { toast.error(errMsg(err, "Übernahme fehlgeschlagen")); }
  };

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach(r => { (g[r.status] = g[r.status] || []).push(r); });
    return g;
  }, [rows]);
  const dayEvents = events.filter(e => e.datum === selected);
  const c = uebersicht.counts || {};

  return (
    <div className="space-y-8" data-testid="fristen-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-[#D32F2F] mb-2">Fristenkontrolle</p>
          <h1 className="font-serif text-4xl tracking-tight">Fristen & Kalender</h1>
          <div className="flex flex-wrap gap-2 mt-3 text-xs" data-testid="fristen-counts">
            <span className="px-2 py-1 rounded bg-[#D32F2F] text-white font-semibold">{c.ueberfaellig || 0} überfällig</span>
            <span className="px-2 py-1 rounded bg-[#F59E0B] text-white font-semibold">{c.heute || 0} heute</span>
            <span className="px-2 py-1 rounded bg-[#0EA5E9] text-white font-semibold">{c.vorfrist || 0} in Vorfrist</span>
            <span className="px-2 py-1 rounded bg-[#F3F4F6] text-[#4B5563]">{rows.filter(r => !r.erledigt).length} offen gesamt</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-0.5">
            <button onClick={() => setView("liste")} className={`px-3 py-1.5 text-xs font-medium rounded-md inline-flex items-center gap-1 ${view === "liste" ? "bg-[#1A2238] text-white" : "text-[#4B5563]"}`} data-testid="view-liste"><ListBullets size={14} /> Liste</button>
            <button onClick={() => setView("kalender")} className={`px-3 py-1.5 text-xs font-medium rounded-md inline-flex items-center gap-1 ${view === "kalender" ? "bg-[#1A2238] text-white" : "text-[#4B5563]"}`} data-testid="view-kalender"><CalendarBlank size={14} /> Kalender</button>
          </div>
          <Button className="bg-[#1A2238] hover:bg-[#2C3859]" onClick={() => setOpen(true)} data-testid="new-frist-btn"><Plus size={16} className="mr-2" /> Neue Frist</Button>
        </div>
      </header>

      <KiAssistent akten={akten} />

      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="fristen-digest-box">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5"><BellRinging size={14} weight="duotone" className="text-[#F59E0B]" /> Tägliche Fristen-E-Mail um 07:00 Uhr</p>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Überfällige, heute fällige Fristen und Vorfristen sowie fällige Wiedervorlagen — an {user?.kanzlei_email || user?.email}.
            {!user?.smtp_host && <span className="text-[#D32F2F]"> Dafür muss der E-Mail-Versand (SMTP) in den Einstellungen eingerichtet sein.</span>}
          </p>
        </div>
        <Switch checked={user?.frist_email_digest !== false} onCheckedChange={async v => { try { const r = await api.patch("/auth/me", { frist_email_digest: v }); setUser(r.data); toast.success(v ? "Tägliche Fristen-Mail aktiv" : "Tägliche Fristen-Mail aus"); } catch (err) { toast.error(errMsg(err, "Einstellung fehlgeschlagen")); } }} data-testid="fristen-digest-switch" />
      </div>

      {/* Fristen automatisch vorschlagen */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid="fristen-vorschlag-box">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex items-center gap-2 text-sm font-medium"><MagicWand size={16} weight="duotone" className="text-[#7C3AED]" /> Fristen für eine Akte automatisch berechnen</div>
          <Select value={vorschlagAkte || "none"} onValueChange={v => { const id = v === "none" ? "" : v; setVorschlagAkte(id); setVorschlaege(null); holeVorschlaege(id); }}>
            <SelectTrigger className="sm:w-[300px] sm:ml-auto" data-testid="vorschlag-akte"><SelectValue placeholder="Akte wählen…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Akte wählen…</SelectItem>
              {akten.map(a => <SelectItem key={a.id} value={a.id}>{a.aktenzeichen} · {a.mandant_name || "—"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {vorschlagBusy && <p className="text-xs text-[#6B7280]">Berechne …</p>}
        {vorschlaege && vorschlaege.vorschlaege.length === 0 && <p className="text-xs text-[#6B7280]" data-testid="vorschlag-leer">Keine neuen Fristen ableitbar (fehlende Daten oder bereits angelegt).</p>}
        {vorschlaege && vorschlaege.vorschlaege.length > 0 && (
          <div className="space-y-2" data-testid="vorschlag-liste">
            {vorschlaege.vorschlaege.map((v, i) => (
              <div key={i} className="flex items-start justify-between gap-3 border border-[#E5E7EB] rounded-md p-3" data-testid={`vorschlag-${v.typ}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{v.titel} <span className="text-[10px] uppercase text-[#6B7280] ml-1">{TYP_LABEL[v.typ]}</span></p>
                  <p className="text-xs text-[#6B7280] font-mono">{fmtD(v.faellig_am)} · Vorfrist {v.vorfrist_tage} Tg. · {v.rechtsgrundlage}</p>
                  {v.beschreibung && <p className="text-xs text-[#4B5563] mt-0.5">{v.beschreibung}</p>}
                </div>
                <Button size="sm" variant="outline" onClick={() => uebernehmen([v])} data-testid={`vorschlag-uebernehmen-${v.typ}`}>Übernehmen</Button>
              </div>
            ))}
            <div className="flex justify-end"><Button size="sm" className="bg-[#1A2238] hover:bg-[#2C3859]" onClick={() => uebernehmen(vorschlaege.vorschlaege)} data-testid="vorschlag-alle">Alle übernehmen</Button></div>
          </div>
        )}
      </div>

      {view === "kalender" ? (
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
          <FristenKalender events={events} monat={monat} onMonat={setMonat} selected={selected} onSelect={setSelected} />
          <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid="kal-tag-detail">
            <p className="font-serif text-lg">{fmtD(selected)}</p>
            {dayEvents.length === 0 && <p className="text-sm text-[#6B7280]">Keine Termine an diesem Tag.</p>}
            {dayEvents.map(e => (
              <div key={`${e.art}-${e.id}`} className="border border-[#E5E7EB] rounded-md p-3" data-testid={`kal-detail-${e.art}`}>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded text-white ${e.art === "frist" ? "bg-[#D32F2F]" : e.art === "vorfrist" ? "bg-[#F59E0B]" : "bg-[#0EA5E9]"}`}>{ART_LABEL[e.art]}</span>
                  <p className={`text-sm font-medium ${e.erledigt ? "line-through text-[#9CA3AF]" : ""}`}>{e.titel}</p>
                </div>
                {e.art === "vorfrist" && <p className="text-xs text-[#6B7280] mt-0.5">Hauptfrist: {fmtD(e.faellig_am)}</p>}
                {e.rechtsgrundlage && <p className="text-xs text-[#6B7280] font-mono">{e.rechtsgrundlage}</p>}
                {e.akte && <Link to={`/app/akten/${e.akte.id}`} className="text-xs text-[#1A2238] hover:underline">{e.akte.aktenzeichen} · {e.akte.mandant_name || "—"}</Link>}
                {e.art === "wiedervorlage" && <Link to="/app/wiedervorlagen" className="block text-xs text-[#0EA5E9] hover:underline mt-1"><BellRinging size={11} className="inline mr-1" />Zur Wiedervorlage</Link>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {rows.length === 0 && (
            <div className="bg-white border border-[#E5E7EB] rounded-lg p-12 text-center">
              <Clock size={40} weight="duotone" className="mx-auto text-[#9CA3AF]" />
              <p className="font-serif text-2xl mt-4">Keine Fristen</p>
              <p className="text-sm text-[#6B7280] mt-1">Lege eine Frist an oder lass sie oben automatisch aus einer Akte berechnen.</p>
            </div>
          )}
          {GROUP_ORDER.filter(k => grouped[k]?.length).map(k => (
            <section key={k} data-testid={`fristen-gruppe-${k}`}>
              <h2 className="text-xs uppercase tracking-wide text-[#6B7280] mb-2 flex items-center gap-2">
                {k === "ueberfaellig" && <Warning size={14} className="text-[#D32F2F]" />} {STATUS_META[k].label} <span className="font-mono">({grouped[k].length})</span>
              </h2>
              <div className="space-y-2">{grouped[k].map(f => <FristCard key={f.id} f={f} onToggle={toggle} onDelete={del} />)}</div>
            </section>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Neue Frist</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label className="overline">Titel</Label>
              <Input required value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} data-testid="frist-titel" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Art</Label>
                <Select value={form.typ} onValueChange={v => setForm(f => ({ ...f, typ: v }))}>
                  <SelectTrigger data-testid="frist-typ"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(TYP_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Priorität</Label>
                <Select value={form.prioritaet} onValueChange={v => setForm(f => ({ ...f, prioritaet: v }))}>
                  <SelectTrigger data-testid="frist-prio"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="niedrig">Niedrig</SelectItem>
                    <SelectItem value="mittel">Mittel</SelectItem>
                    <SelectItem value="hoch">Hoch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Fällig am</Label>
                <Input required type="date" value={form.faellig_am} onChange={e => setForm(f => ({ ...f, faellig_am: e.target.value }))} data-testid="frist-datum" />
              </div>
              <div>
                <Label className="overline">Vorfrist (Tage vorher)</Label>
                <Input type="number" min="0" max="365" value={form.vorfrist_tage} onChange={e => setForm(f => ({ ...f, vorfrist_tage: e.target.value }))} data-testid="frist-vorfrist" />
              </div>
            </div>
            <div>
              <Label className="overline">Rechtsgrundlage (optional)</Label>
              <Input value={form.rechtsgrundlage} onChange={e => setForm(f => ({ ...f, rechtsgrundlage: e.target.value }))} placeholder="z. B. § 67 OWiG" data-testid="frist-rechtsgrundlage" />
            </div>
            <div>
              <Label className="overline">Beschreibung</Label>
              <Textarea rows={2} value={form.beschreibung} onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))} data-testid="frist-beschreibung" />
            </div>
            <div>
              <Label className="overline">Akte (optional)</Label>
              <Select value={form.akte_id || "none"} onValueChange={v => setForm(f => ({ ...f, akte_id: v === "none" ? "" : v }))}>
                <SelectTrigger data-testid="frist-akte"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Keine —</SelectItem>
                  {akten.map(a => <SelectItem key={a.id} value={a.id}>{a.aktenzeichen} – {a.mandant_name || ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-[#1A2238] hover:bg-[#2C3859]" data-testid="frist-submit">{saving ? "Speichert…" : "Anlegen"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
